import React, { useState } from "react";
import { Upload, Button, message, Card, Typography, Space } from "antd";
import { FaUpload, FaTimes, FaCheckCircle, FaFileAlt } from "react-icons/fa";
import { v4 as uuid } from "uuid";
import * as API from "../../../services/apis";

const { Text } = Typography;

const extensionToMimeType = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".bmp": "image/bmp",
  ".webp": "image/webp",
  ".svg": "image/svg+xml",
};
const typeToMimeType = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  bmp: "image/bmp",
  webp: "image/webp",
  svg: "image/svg+xml",
};

const DocumentUpload = ({
  title,
  subtitle = "",
  required = false,
  onUpload,
  onRemove,
  uploadedFile = null,
  documentType,
  acceptType = "",
  maxSizeInMB = 10,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);

  const uploadToAzure = async (file) => {
    setUploading(true);
    let documentUrl = null;

    try {
      // 1. Get the pre-signed URL
      const fileUrlResponse = await API.getFileUrl({
        blobName: `${file.name}_${uuid()}`,
        source: {
          name: process.env.REACT_APP_ENVIRONMENT,
        },
      });

      const presignedUrl = fileUrlResponse.data?.data?.document;
      if (!presignedUrl) {
        throw new Error("Presigned URL is missing in response");
      }

      // 2. Upload file to Azure
      await API.uploadFile({
        data: file,
        url: presignedUrl,
      });

      // 3. Set the document URL
      documentUrl = presignedUrl;
      message.success(`${file.name} uploaded successfully`);

      // 4. Notify parent with upload result
      if (onUpload) {
        // Pass
        onUpload({
          file,
          url: documentUrl,
          fileName: file.name,
        });
      }

      return documentUrl;
    } catch (err) {
      console.error("Upload failed:", err);
      message.error(`Upload failed for ${file.name}: ${err.message}`);
      throw err; // Re-throw so caller can handle
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = async (uploadedFile) => {
    // If documentType is provided, convert to MIME types and validate
    if (documentType && Array.isArray(documentType)) {
      const allowedMimeTypes = documentType
        .map((type) => typeToMimeType[type])
        .filter(Boolean);

      if (!allowedMimeTypes.includes(uploadedFile.type)) {
        message.error(
          `Please upload one of these file types: ${documentType.join(", ")}!`
        );
        return false;
      }
    } else if (acceptType) {
      // Fallback: convert accept extensions to MIME types for validation
      const acceptedExtensions = acceptType
        .split(",")
        .map((type) => type.trim());
      const allowedMimeTypes = acceptedExtensions
        .map((ext) => extensionToMimeType[ext])
        .filter(Boolean);

      if (!allowedMimeTypes.includes(uploadedFile.type)) {
        message.error(
          `Please upload one of these file types: ${acceptedExtensions.join(
            ", "
          )}`
        );
        return false;
      }
    }

    const isValidSize = uploadedFile.size / 1024 / 1024 < maxSizeInMB;
    if (!isValidSize) {
      message.error(`File must be smaller than ${maxSizeInMB}MB!`);
      return false;
    }

    // Upload to Azure
    try {
      await uploadToAzure(uploadedFile);
    } catch (error) {
      console.error("Upload process failed:", error);
    }

    return false; // Prevent auto upload
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileName = () => {
    if (uploadedFile?.fileName) return uploadedFile.fileName;
    return "Unknown file";
  };

  const getFileSize = () => {
    if (uploadedFile?.size) return formatFileSize(uploadedFile.size);
    if (typeof uploadedFile === "string") {
      return "";
    }
    return "";
  };

  const hasFile = uploadedFile && (uploadedFile.document || uploadedFile.documentUrl);

  // Generate accept string from documentType or use acceptType fallback
  const getAcceptString = () => {
    if (documentType && Array.isArray(documentType)) {
      return documentType.map((type) => `.${type}`).join(",");
    }
    return acceptType;
  };

  return (
    <Card
      size="small"
      style={{
        borderStyle: hasFile ? "solid" : "dashed",
        borderColor: hasFile ? "#52c41a" : "#d9d9d9",
        backgroundColor: hasFile ? "#f6ffed" : "#fafafa",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "60px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
          }}
        >
          {hasFile ? (
            <FaCheckCircle
              style={{
                fontSize: "24px",
                color: "#52c41a",
              }}
            />
          ) : (
            <FaFileAlt
              style={{
                fontSize: "24px",
                color: "#8c8c8c",
              }}
            />
          )}

          <Space direction="vertical" size={0} style={{ flex: 1 }}>
            <Text strong>
              {title}
              {required && <Text type="danger"> *</Text>}
            </Text>
            {hasFile ? (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {getFileName()} {getFileSize() && `(${getFileSize()})`}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {subtitle}
              </Text>
            )}
          </Space>
        </div>

        <Space>
          {hasFile && !disabled && (
            <Button
              type="text"
              icon={<FaTimes />}
              onClick={handleRemove}
              size="small"
              danger
            />
          )}

          {!hasFile && !disabled && (
            <Upload
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept={getAcceptString()}
              disabled={disabled || uploading}
            >
              <Button
                type="dashed"
                loading={uploading}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <FaUpload />
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </Upload>
          )}
        </Space>
      </div>
    </Card>
  );
};

export default DocumentUpload;
